import { TextField, type TextFieldProps } from "@octopus-energy/coral/atoms"
import { useField } from "formik"
import { friendlyFormatIBAN } from "ibantools"
import { useTranslations } from "next-intl"

type FormikIbanFieldProps = Omit<
    TextFieldProps,
    "type" | "value" | "onChange" | "onBlur" | "error" | "errorMessage"
> & {
    label?: TextFieldProps["label"]
    name: string
}

const format = (raw: string) => friendlyFormatIBAN(raw) ?? raw

export function FormikIbanField({ name, label, ...props }: FormikIbanFieldProps) {
    const t = useTranslations("FormikIbanField")
    const [field, meta, helpers] = useField(name)

    const applyRaw = (raw: string) => void helpers.setValue(raw)

    return (
        <TextField
            {...props}
            type="text"
            name={name}
            label={label ?? t("label")}
            value={format(field.value)}
            onChange={(e) => applyRaw(e.target.value.replace(/\s/g, ""))}
            onBlur={field.onBlur}
            error={meta.touched && !!meta.error}
            errorMessage={meta.touched ? meta.error : undefined}
            inputProps={{
                // Prevent the browser from writing raw pasted text to the DOM before
                // React can reconcile it. Without this, if the pasted value equals the
                // current Formik value, no re-render fires and the unformatted text persists.
                onPaste: (e) => {
                    e.preventDefault()
                    applyRaw(e.clipboardData.getData("text/plain").replace(/\s/g, ""))
                },
            }}
        />
    )
}

FormikIbanField.messages = ["FormikIbanField"] as const
